import { Link as RouterLink } from "react-router-dom";
import { Link, Chip, Box, IconButton } from "@mui/material";
import {
  yearFirstDateTimeFormat,
  fTransactionYearFirstDateTime,
} from "src/utils/formatTime";
import { TransactionActionMenu } from "./TransactionActionMenu";
import Tooltip from "@mui/material/Tooltip";
import { Icon } from "@iconify/react";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import copyFill from "@iconify/icons-eva/copy-fill";

export const transactionsListColumns = (isReadOnly) => [
  {
    field: "Actions",
    width: 50,
    sortable: false,
    filterable: false,
    headerName: "",
    renderCell: ({ id, row }) => {
      return <TransactionActionMenu id={id} row={row} />;
    },
  },
  {
    field: "transactionId",
    headerName: "Transaction ID",
    width: 120,
    renderCell: ({ value, id, row }) => {
      const isActive =
        !row.stoptime || row.stoptime === null || row.stoptime === "";
      const statusIcon = isActive ? clockFill : checkmarkCircle2Fill;
      const statusColor = isActive ? "#ed6c02" : "#2e7d32";
      const statusTooltip = isActive
        ? "Active Transaction"
        : "Completed Transaction";

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={statusTooltip} placement="top">
            <Icon
              icon={statusIcon}
              width={16}
              height={16}
              style={{ color: statusColor, flexShrink: 0 }}
            />
          </Tooltip>
          {isReadOnly ? (
            <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>
              {value}
            </span>
          ) : (
            <Link
              underline="none"
              component={RouterLink}
              to={`/transactions/${id}`}
              style={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#1976d2",
                textDecoration: "none",
              }}
            >
              {value}
            </Link>
          )}
        </Box>
      );
    },
  },
  {
    field: "paymentId",
    headerName: "Payment ID",
    width: 120,
    renderCell: ({ value, row }) => {
      if (!value) return <span style={{ color: "#999" }}>—</span>;

      const handleCopyPaymentId = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard
          .writeText(value)
          .then(() => {
            // You could add a toast notification here if needed
          })
          .catch(() => {
            // Handle copy error if needed
          });
      };

      const displayValue =
        value.length > 20 ? `${value.substring(0, 10)}...` : value;
      const showCopyIcon = value.length > 20;

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Link
            component={RouterLink}
            to={`/refund-details/${value}`}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {displayValue}
          </Link>
          {showCopyIcon && (
            <Tooltip title="Copy full Payment ID">
              <IconButton
                size="small"
                onClick={handleCopyPaymentId}
                sx={{
                  p: 0.5,
                  ml: 0.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Icon icon={copyFill} width={14} height={14} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    field: "email",
    headerName: "Email",
    width: 120,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "phone",
    headerName: "Phone no.",
    width: 110,
  },
  {
    field: "tag",
    headerName: "Tag",
    width: 120,
    hide: true,
    renderCell: ({ value }) =>
      value ? <span>{value}</span> : <span style={{ color: "#999" }}>—</span>,
  },
  {
    field: "parenttag",
    headerName: "Parent Tag",
    width: 120,
    hide: true,
    renderCell: ({ value }) =>
      value ? (
        <Chip
          label={value}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500, fontSize: "0.75rem" }}
        />
      ) : (
        <span style={{ color: "#999" }}>—</span>
      ),
  },
  {
    field: "chargepoint_id",
    headerName: "ChargeBox ID",
    width: 120,
  },
  {
    field: "connectorId",
    headerName: "Connector",
    width: 100,
  },
  {
    field: "mac",
    headerName: "Vehicle Mac Id",
    width: 130,
    renderCell: ({ value }) => {
      if (!value) return <span style={{ color: "#999" }}>—</span>;
      return (
        <Tooltip title={value} placement="top">
          <span>{value}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "kw_consumed",
    headerName: "kWh Consumed",
    width: 100,
    type: "number",
    valueGetter: ({ row }) => {
      const startValue = parseFloat(row.start_meter_value || 0);
      const stopValue = parseFloat(row.stop_meter_value || 0);
      const currentValue = parseFloat(row.current_meter_value || 0);

      // Use stop meter value if available, otherwise use current meter value
      const endValue = stopValue > 0 ? stopValue : currentValue;

      // If no valid end value or end value is less than start value, return 0
      if (endValue <= startValue) {
        return 0;
      }

      // Calculate kW consumed (convert from Wh to kWh)
      const kwConsumed = (endValue - startValue) / 1000;
      return Number(kwConsumed.toFixed(3));
    },
    valueFormatter: ({ value }) => `${Number(value).toFixed(3)}`,
  },
  {
    field: "price",
    headerName: "Price",
    width: 130,
    valueGetter: ({ row }) => {
      const pricePerKw = parseFloat(row.pricePerKw || 0);
      const pricePerMin = parseFloat(row.pricePerMin || 0);
      if (pricePerKw > 0) return pricePerKw;
      if (pricePerMin > 0) return pricePerMin;
      return 0;
    },
    renderCell: ({ row }) => {
      const pricePerKw = parseFloat(row.pricePerKw || 0);
      const pricePerMin = parseFloat(row.pricePerMin || 0);
      if (pricePerKw > 0) return `${pricePerKw.toFixed(2)} /kWh`;
      if (pricePerMin > 0) return `${pricePerMin.toFixed(2)} /min`;
      return "0.00";
    },
  },
  {
    field: "payment_type",
    headerName: "Payment Type",
    width: 100,
    renderCell: ({ value }) => value || "—",
  },
  {
    field: "totalAmountPaid",
    headerName: "Amount Paid",
    width: 90,
    type: "number",
    valueGetter: ({ value }) => Number(parseFloat(value || 0).toFixed(2)),
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "consumptionCost",
    headerName: "Consumption Cost",
    width: 140,
    type: "number",
    valueGetter: ({ row }) => {
      const total = parseFloat(row.totalAmountCharged || 0);
      const tax = parseFloat(row.taxAmount || 0);
      const pg = parseFloat(row.transactionAmount || 0);
      return Number((total - tax - pg).toFixed(2));
    },
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "taxAmount",
    headerName: "Tax Amount",
    width: 100,
    type: "number",
    valueGetter: ({ value }) => Number(parseFloat(value || 0).toFixed(2)),
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "transactionAmount",
    headerName: "PG Charges",
    width: 100,
    type: "number",
    valueGetter: ({ value }) => Number(parseFloat(value || 0).toFixed(2)),
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "totalAmountCharged",
    headerName: "Charging Cost",
    width: 120,
    type: "number",
    valueGetter: ({ value }) => Number(parseFloat(value || 0).toFixed(2)),
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "totalRefund",
    headerName: "Refund / Wallet balance",
    width: 130,
    type: "number",
    valueGetter: ({ value }) => Number(parseFloat(value || 0).toFixed(2)),
    valueFormatter: ({ value }) => Number(value).toFixed(2),
  },
  {
    field: "starttime",
    headerName: "Start Time",
    type: "date",
    width: 130,
    renderCell: ({ value, row }) => {
      // Use new conversion function for non-Razorpay transactions
      const displayValue = fTransactionYearFirstDateTime(value);
      return (
        <Tooltip title={displayValue} placement="top">
          <span>{displayValue}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "stoptime",
    headerName: "Stop Time",
    type: "date",
    width: 130,
    renderCell: ({ value, row }) => {
      if (!value) return <span>—</span>;
      // Use new conversion function for non-Razorpay transactions
      const displayValue = fTransactionYearFirstDateTime(value);
      return (
        <Tooltip title={displayValue} placement="top">
          <span>{displayValue}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "notes",
    headerName: "notes",
    width: 150,
  },
];

export const chartLabelConstants = {
  "Current.Export": "Instantaneous current flow from EV",
  "Current.Import": "Instantaneous current flow to EV",
  "Current.Offered": "Maximum current offered to EV",
  "Energy.Active.Export.Register": "Energy exported by EV (Wh or kWh)",
  "Energy.Active.Import.Register": "Energy imported by EV (Wh or kWh)",
  "Energy.Reactive.Export.Register":
    "Reactive energy exported by EV (varh or kvarh)",
  "Energy.Reactive.Import.Register":
    "Reactive energy imported by EV (varh or kvarh)",
  "Energy.Active.Export.Interval": "Energy exported by EV (Wh or kWh)",
  "Energy.Active.Import.Interval": "Energy imported by EV (Wh or kWh)",
  "Energy.Reactive.Export.Interval":
    "Reactive energy exported by EV. (varh or kvarh)",
  "Energy.Reactive.Import.Interval":
    "Reactive energy imported by EV. (varh or kvarh)",
  Frequency: "Instantaneous reading of powerline frequency",
  "Power.Active.Export": "Instantaneous active power exported by EV. (W or kW)",
  "Power.Active.Import": "Instantaneous active power imported by EV. (W or kW)",
  "Power.Factor": "Instantaneous power factor of total energy flow",
  "Power.Offered": "Maximum power offered to EV",
  "Power.Reactive.Export":
    "Instantaneous reactive power exported by EV. (var or kvar)",
  "Power.Reactive.Import":
    "Instantaneous reactive power imported by EV. (var or kvar)",
  RPM: "Fan speed in RPM",
  Soc: "State of charge of charging vehicle in percentage",
  SoC: "State of charge of charging vehicle in percentage",
  Temperature: "Temperature reading inside Charge Point",
  Voltage: "Instantaneous AC RMS supply voltage",
};

export const chartTypeToShow = [
  "Temperature",
  "Voltage",
  "Current.Import",
  "Power.Active.Import",
  "SoC",
];
